// StreakCalendar.jsx
import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const formatDate = (date) => new Date(date).toISOString().split("T")[0];

const generateDateRange = (startDate, endDate) => {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);
    while (current <= end) {
        dates.push(formatDate(current));
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

// "none"    → dark gray  (no activity)
// "pending" → light gray (active but streak < 7)
// "green"   → 7+ day streak
// "blue"    → 30+ day streak
// "purple"  → 90+ day streak
const COLOR_MAP = {
    none:    { cell: "bg-gray-800 border-gray-700",                          label: "No activity"   },
    pending: { cell: "bg-gray-500 border-gray-400",                          label: "< 7 days"      },
    green:   { cell: "bg-green-500 border-green-400 shadow-sm shadow-green-500/50",   label: "7+ days"       },
    blue:    { cell: "bg-blue-500 border-blue-400 shadow-sm shadow-blue-500/50",      label: "30+ days"      },
    purple:  { cell: "bg-purple-500 border-purple-400 shadow-sm shadow-purple-500/50", label: "90+ days"     },
};

const StreakCalendar = ({
    title = "Activity Streak",
    data = [],
    startDate,
    endDate,
    dateField = "todoDate",
    eligibleMilestones = [],
    onRequestApproval,
    onStreakChange,
}) => {
    const [tooltip, setTooltip] = useState(null);

    const { calendarDays, currentStreak, longestStreak, totalActiveDays } = useMemo(() => {
        const allDates = generateDateRange(startDate, endDate);
        const activeDates = new Set(
            data.filter((i) => i?.[dateField]).map((i) => formatDate(i[dateField]))
        );

        // Step 1: Build streaks — collect runs of consecutive active dates
        // Each streak is { startIndex, length }
        const streaks = [];
        let streakStartIdx = null;
        let streakLen = 0;

        const finalizeStreak = () => {
            if (streakStartIdx !== null && streakLen > 0) {
                streaks.push({ startIndex: streakStartIdx, length: streakLen });
            }
            streakStartIdx = null;
            streakLen = 0;
        };

        for (let i = 0; i < allDates.length; i++) {
            if (activeDates.has(allDates[i])) {
                if (streakStartIdx === null) streakStartIdx = i;
                streakLen++;
            } else {
                finalizeStreak();
            }
        }
        finalizeStreak();

        // Step 2: For each streak, assign colors day-by-day with progressive upgrades
        // Days 1–6   → "pending" (light gray)
        // Day 7      → upgrade days 0–6 to "green", continue as green
        // Day 30     → upgrade days 0–29 to "blue", continue as blue
        // Day 90     → upgrade days 0–89 to "purple", continue as purple
        const dayColorMap = {}; // date → color key

        for (const { startIndex, length } of streaks) {
            // Assign initial colors progressively
            const streakColors = []; // per-day color within this streak
            let currentColor = "pending";

            for (let d = 0; d < length; d++) {
                // Check upgrade thresholds at exactly day 7, 30, 90 (1-indexed)
                if (d + 1 === 90) {
                    currentColor = "purple";
                    for (let back = 0; back < d; back++) streakColors[back] = "purple";
                } else if (d + 1 === 30) {
                    currentColor = "blue";
                    for (let back = 0; back < d; back++) streakColors[back] = "blue";
                } else if ((d + 1) % 7 === 0 && currentColor !== "blue" && currentColor !== "purple") {
                    // complete another 7-day block → mark exactly the last 7 days green
                    for (let back = d - 6; back <= d; back++) streakColors[back] = "green";
                    currentColor = "pending"; // reset: next days start as pending again until next 7-block
                }
                streakColors.push(currentColor);
            }

            // Write into the global day color map
            for (let d = 0; d < length; d++) {
                dayColorMap[allDates[startIndex + d]] = streakColors[d];
            }
        }

        // Step 3: Compute current streak (counting back from the last date)
        let currentStreakValue = 0;
        for (let i = allDates.length - 1; i >= 0; i--) {
            if (activeDates.has(allDates[i])) currentStreakValue++;
            else break;
        }

        // Step 4: Longest streak
        let longestStreakValue = 0;
        for (const { length } of streaks) {
            longestStreakValue = Math.max(longestStreakValue, length);
        }

        // Step 5: Build calendar array
        let activeStreakCounter = 0;
        let inactiveStreakCounter = 0;
        const calendar = allDates.map((date) => {
            const active = activeDates.has(date);
            const colorKey = active ? (dayColorMap[date] ?? "pending") : "none";
            if (active) {
                activeStreakCounter++;
                inactiveStreakCounter = 0;
            } else {
                inactiveStreakCounter++;
                activeStreakCounter = 0;
            }
            const streakDay = active ? activeStreakCounter : inactiveStreakCounter;
            return { date, active, colorKey, streakDay };
        });

        return {
            calendarDays: calendar,
            currentStreak: currentStreakValue,
            longestStreak: longestStreakValue,
            totalActiveDays: activeDates.size,
        };
    }, [data, startDate, endDate, dateField]);

    useEffect(() => {
        onStreakChange?.(currentStreak);
    }, [currentStreak]);

    return (
        <div className="rounded-xl border-t border-cyan-100 bg-gray-900/80 p-4 space-y-4">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-black text-sm text-white">{title}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                        {startDate?.substring(0, 10)} → {endDate?.substring(0, 10)}
                    </div>
                </div>
                <div className="flex gap-3">
                    {[
                        { label: "Current", value: currentStreak, color: "text-cyan-300" },
                        { label: "Best",    value: longestStreak, color: "text-purple-300" },
                        { label: "Active",  value: totalActiveDays, color: "text-green-300" },
                    ].map((s) => (
                        <div key={s.label} className="text-center">
                            <div className={`font-black text-lg leading-none ${s.color}`}>
                                {s.value}
                            </div>
                            <div className="text-[9px] text-gray-500">{s.label}</div>
                        </div>
                    ))}
                </div>
            </div>
            {eligibleMilestones?.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                    {eligibleMilestones.map((days) => (
                        <button
                            key={days}
                            onClick={() => onRequestApproval?.(days)}
                            className="text-xs w-fit px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-black"
                        >
                            🎉 Send {days}-Day Approval Request
                        </button>
                    ))}
                </div>
            )}

            {/* Calendar Grid */}
            <div className="grid grid-cols-5 lg:grid-cols-10 gap-1.5">
                {calendarDays.map((day) => {
                    const cfg = COLOR_MAP[day.colorKey];
                    return (
                        <div key={day.date} className="relative flex flex-col items-center gap-0.5 border border-gray-700 py-1">
                            <span className="text-[7px] font-bold leading-none text-yellow-200 tracking-wide">
                                {new Date(day.date).toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}
                                {` · ${day.streakDay}`}
                            </span>

                            <motion.button
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                                onMouseEnter={() => setTooltip(day)}
                                onMouseLeave={() => setTooltip(null)}
                                onTouchStart={() => setTooltip(day)}
                                onTouchEnd={() => setTimeout(() => setTooltip(null), 1600)}
                                className={`w-full aspect-square rounded-md border transition-all duration-200
                                    ${cfg.cell}
                                    hover:bg-amber-400 hover:border-amber-300 hover:shadow-amber-400/60 hover:shadow-md`}
                            />

                            <span className="text-[7px] leading-none text-cyan-200">
                                {day.date.split("-").reverse().join("/")}
                            </span>

                            {/* Inline tooltip on hover */}
                            {tooltip?.date === day.date && (
                                <motion.div
                                    initial={{ opacity: 0, y: -4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute bottom-full mb-1.5 z-50 bg-gray-900 border border-amber-400/40 rounded-lg px-2 py-1.5 text-[10px] text-center whitespace-nowrap shadow-lg"
                                >
                                    <div className="text-amber-300 font-black">
                                        {new Date(day.date).toLocaleDateString("en-US", { weekday: "long" })}
                                    </div>
                                    <div className="text-white/80">
                                        {day.date.split("-").reverse().join("/")}
                                    </div>
                                    <div className="mt-0.5">
                                        {day.active
                                            ? <span className="text-green-400">🔥 Day {day.streakDay}</span>
                                            : <span className="text-gray-500">No activity</span>}
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Tooltip */}
            <AnimatePresence>
                {tooltip && (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="bg-gray-800 border border-gray-600 rounded-xl p-2.5 text-[11px] text-center mx-auto max-w-[180px]"
                    >
                        <div className="text-white font-bold">{tooltip.date}</div>
                        {tooltip.active ? (
                            <div className="text-green-400">
                                {tooltip.colorKey === "purple" ? "💜 90+ day streak!" :
                                 tooltip.colorKey === "blue"   ? "💙 30+ day streak!" :
                                 tooltip.colorKey === "green"  ? "🔥 7+ day streak!" :
                                                                 "⬜ Building streak…"}
                            </div>
                        ) : (
                            <div className="text-gray-500">No activity</div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 text-[10px] text-gray-400">
                <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-gray-800 border border-gray-700" />
                    No activity
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-gray-500 border border-gray-400" />
                    &lt; 7 days
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-green-500" />
                    7–29 days
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-blue-500" />
                    30–89 days
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-purple-500" />
                    90+ days
                </div>
            </div>
        </div>
    );
};

export default StreakCalendar;


